/*
 * Import report, skin install, batch picker, import banner and summary (W2-09 publish-import; DESIGN.md §4.17).
 * Design tokens only; every class and status pairs an icon with a word.
 */

.dialog {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}

.muted {
  color: var(--color-text-muted);
  font-size: var(--text-xs);
}

.summaryLine {
  margin: 0;
  color: var(--color-text-muted);
  font-size: var(--text-sm);
  overflow-wrap: anywhere;
}

.status {
  display: flex;
  align-items: flex-start;
  gap: var(--space-2);
  margin: 0;
  font-size: var(--text-md);
}

.status > svg {
  flex: none;
  margin-top: 2px;
}

.status[data-ok='true'] > svg {
  color: var(--color-success);
}

.status[data-ok='false'] > svg {
  color: var(--color-severity-fatal);
}

.classes {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.classBlock {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}

/* Actions under a class's entries start where the entries start (UX-081: the button was centred alone). */
.afterRow {
  display: flex;
  justify-content: flex-start;
  padding-left: var(--space-4);
}

.classTitle {
  display: flex;
  align-items: center;
  gap: var(--space-1);
  margin: 0;
  font-size: var(--text-xs);
  font-weight: var(--weight-semibold);
  text-transform: uppercase;
  letter-spacing: 0.02em;
  color: var(--color-text-muted);
}

.classBlock[data-class='changed'] .classTitle > svg {
  color: var(--color-severity-error);
}

.classBlock[data-class='flagged'] .classTitle > svg {
  color: var(--color-warning);
}

.classBlock[data-class='normalized'] .classTitle > svg {
  color: var(--color-info);
}

.classBlock[data-class='exact'] .classTitle > svg {
  color: var(--color-success);
}

.classBlock[data-class='rpeOnly'] .classTitle > svg {
  color: var(--color-badge-phira-ignores);
}

.entries {
  list-style: none;
  margin: 0;
  padding: 0 0 0 var(--space-4);
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  font-size: var(--text-sm);
}

.entry {
  display: flex;
  flex-direction: column;
  gap: var(--space-0);
  overflow-wrap: anywhere;
}

.entryWhere {
  /* Readable locations (UX-081: 10 px at 4.3:1 before). */
  color: var(--color-text-muted);
  font-family: var(--font-mono);
  font-size: var(--text-xs);
}

.radioList {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  margin: 0;
  padding: 0;
  border: none;
}

.radioLegend {
  padding: 0;
  margin-bottom: var(--space-1);
  font-size: var(--text-sm);
  color: var(--color-text-muted);
}

.radio {
  display: flex;
  align-items: flex-start;
  gap: var(--space-2);
  padding: var(--space-1) var(--space-2);
  border-radius: var(--radius-sm);
  cursor: pointer;
}

.radio:hover {
  background: var(--color-surface-hover);
}

.radio input {
  margin-top: 3px;
  accent-color: var(--color-accent);
}

.radio input:focus-visible {
  outline: var(--focus-ring-width) solid var(--color-focus-ring);
  outline-offset: var(--focus-ring-offset);
}

.radio[data-disabled='true'] {
  cursor: default;
  color: var(--color-text-disabled);
}

.radioText {
  display: flex;
  flex-direction: column;
  gap: var(--space-0);
  min-width: 0;
}

.radioLabel {
  font-size: var(--text-md);
}

.radioHelp {
  font-size: var(--text-xs);
  color: var(--color-text-muted);
}

.mergeOptions {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: var(--space-2);
  padding-left: var(--space-6);
}

.mergeOptions > * {
  min-width: 140px;
}

.footer {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: var(--space-2);
  width: 100%;
}

.footerStart {
  margin-right: auto;
  display: flex;
  gap: var(--space-2);
}

/* ---------- skin install ---------- */

.skinHead {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}

.skinName {
  margin: 0;
  font-size: var(--text-lg);
  font-weight: var(--weight-semibold);
}

.images {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(88px, 1fr));
  gap: var(--space-2);
}

.image {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-1);
  padding: var(--space-1);
  border-radius: var(--radius-sm);
  background: var(--color-surface-sunken);
  font-size: var(--text-2xs);
  color: var(--color-text-muted);
}

.image img {
  width: 72px;
  height: 72px;
  object-fit: contain;
}

.messages {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}

.message {
  display: flex;
  align-items: flex-start;
  gap: var(--space-2);
  font-size: var(--text-sm);
}

.message > svg {
  flex: none;
  margin-top: 2px;
}

.message[data-kind='error'] > svg {
  color: var(--color-severity-fatal);
}

.message[data-kind='warning'] > svg {
  color: var(--color-warning);
}

/* ---------- batch picker ---------- */

.batchList {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  max-height: 320px;
  overflow: auto;
}

.batchRow {
  display: flex;
  align-items: center;
  gap: var(--space-2);
}

.batchRow > :first-child {
  flex: 1;
  min-width: 0;
}

/* ---------- banner ---------- */

.banner {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-2) var(--space-3);
  border-radius: var(--radius-md);
  background: var(--color-info-muted);
  color: var(--color-text);
}

.banner > svg {
  flex: none;
  color: var(--color-info);
}

.bannerText {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  font-size: var(--text-sm);
}

.bannerTitle {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.bannerActions {
  flex: none;
  display: flex;
  gap: var(--space-1);
}

/* ---------- summary ---------- */

.outcomes {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.outcome {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  padding: var(--space-2);
  border-radius: var(--radius-sm);
  background: var(--color-surface-2);
}

.outcomeHead {
  display: flex;
  align-items: flex-start;
  gap: var(--space-2);
}

.outcomeHead > svg {
  flex: none;
  margin-top: 2px;
}

.outcome[data-kind='project'] .outcomeHead > svg,
.outcome[data-kind='merged'] .outcomeHead > svg,
.outcome[data-kind='skin'] .outcomeHead > svg {
  color: var(--color-success);
}

.outcome[data-kind='failed'] .outcomeHead > svg {
  color: var(--color-severity-error);
}

.outcome[data-kind='cancelled'] .outcomeHead > svg,
.outcome[data-kind='media'] .outcomeHead > svg,
.outcome[data-kind='batch'] .outcomeHead > svg {
  color: var(--color-text-muted);
}

.outcomeText {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: var(--space-0);
  overflow-wrap: anywhere;
}

.outcomeName {
  font-weight: var(--weight-medium);
}

.outcomeActions {
  flex: none;
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-1);
}

.details {
  padding-left: var(--space-6);
}

/* ---------- drop zone ---------- */

.dropZone {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-4);
  border: 2px dashed var(--color-border-strong);
  border-radius: var(--radius-lg);
  background: var(--color-surface-sunken);
  color: var(--color-text-muted);
  text-align: center;
  font-size: var(--text-sm);
}

.dropZone[data-over='true'] {
  border-color: var(--color-accent);
  background: var(--color-accent-muted);
  color: var(--color-text);
}

.dropButtons {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: var(--space-2);
}
